/**
 * Password Change Modal Component
 * Allows users to change their password, with forced change support
 */

import { useState } from 'react';
import { Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/Common/Button';
import { ErrorMessage } from '@/components/Common/ErrorMessage';
import { dbAuthService } from '@/services/dbAuthService';

interface PasswordChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  isForced?: boolean; // If true, user must change password before continuing
}

export function PasswordChangeModal({
  isOpen,
  onClose,
  isForced = false,
}: PasswordChangeModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setIsLoading(true);

    try {
      await dbAuthService.changePassword({
        currentPassword: currentPassword,
        newPassword: newPassword,
      });

      setSuccess(true);

      // If not forced, close modal after 1.5 seconds
      if (!isForced) {
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        }, 1500);
      } else {
        // If forced, reload the page to refresh user state
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (err) {
      console.error('Password change failed:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to change password. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isForced && !isLoading) {
      onClose();
      setError(null);
      setSuccess(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center
        bg-black/50 p-4"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl
          bg-bg-primary shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-border-default bg-bg-secondary p-6">
          <div className="flex items-center gap-3">
            <div
              className="rounded-lg bg-warning/10 p-2
                text-warning"
            >
              <Lock className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-text-primary">
                Change Password
              </h2>
              {isForced && (
                <p className="mt-1 text-sm text-text-secondary">
                  You must change your password before continuing
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Warning message for forced change */}
          {isForced && !success && (
            <div
              className="mb-6 flex items-start gap-3 rounded-lg
                bg-warning/10 p-4"
            >
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-warning" />
              <div className="text-sm text-text-primary">
                <p className="font-semibold">Password change required</p>
                <p className="mt-1 text-text-secondary">
                  For security reasons, you must change your default
                  password before accessing the application.
                </p>
              </div>
            </div>
          )}

          {/* Success message */}
          {success && (
            <div
              className="mb-6 flex items-start gap-3 rounded-lg
                bg-success/10 p-4"
            >
              <CheckCircle className="h-5 w-5 flex-shrink-0 text-success" />
              <div className="text-sm text-text-primary">
                <p className="font-semibold">Password changed successfully</p>
                <p className="mt-1 text-text-secondary">
                  {isForced
                    ? 'Reloading application...'
                    : 'Your password has been updated.'}
                </p>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-6">
              <ErrorMessage message={error} severity="error" />
            </div>
          )}

          {!success && (
            <div className="space-y-4">
              {/* Current password */}
              <div>
                <label
                  htmlFor="current-password"
                  className="mb-1 block text-sm font-medium text-text-primary"
                >
                  Current Password
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-3 top-1/2 h-5 w-5
                      -translate-y-1/2 text-text-tertiary"
                  />
                  <input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    disabled={isLoading}
                    className="w-full rounded-lg border border-border-default
                      bg-bg-primary py-2.5 pl-10 pr-4 text-text-primary
                      placeholder-text-tertiary transition-colors
                      focus:border-stellar-primary focus:outline-none
                      focus:ring-2 focus:ring-stellar-primary/20
                      disabled:cursor-not-allowed disabled:opacity-50"
                    required
                    autoComplete="current-password"
                    autoFocus
                  />
                </div>
              </div>

              {/* New password */}
              <div>
                <label
                  htmlFor="new-password"
                  className="mb-1 block text-sm font-medium text-text-primary"
                >
                  New Password
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-3 top-1/2 h-5 w-5
                      -translate-y-1/2 text-text-tertiary"
                  />
                  <input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min. 8 characters)"
                    disabled={isLoading}
                    className="w-full rounded-lg border border-border-default
                      bg-bg-primary py-2.5 pl-10 pr-4 text-text-primary
                      placeholder-text-tertiary transition-colors
                      focus:border-stellar-primary focus:outline-none
                      focus:ring-2 focus:ring-stellar-primary/20
                      disabled:cursor-not-allowed disabled:opacity-50"
                    required
                    autoComplete="new-password"
                    minLength={8}
                  />
                </div>
                <p className="mt-1 text-xs text-text-tertiary">
                  Minimum 8 characters
                </p>
              </div>

              {/* Confirm password */}
              <div>
                <label
                  htmlFor="confirm-password"
                  className="mb-1 block text-sm font-medium text-text-primary"
                >
                  Confirm New Password
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-3 top-1/2 h-5 w-5
                      -translate-y-1/2 text-text-tertiary"
                  />
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    disabled={isLoading}
                    className="w-full rounded-lg border border-border-default
                      bg-bg-primary py-2.5 pl-10 pr-4 text-text-primary
                      placeholder-text-tertiary transition-colors
                      focus:border-stellar-primary focus:outline-none
                      focus:ring-2 focus:ring-stellar-primary/20
                      disabled:cursor-not-allowed disabled:opacity-50"
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  isLoading={isLoading}
                  className="flex-1"
                  disabled={isLoading}
                >
                  {isLoading ? 'Changing Password...' : 'Change Password'}
                </Button>
                {!isForced && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                )}
              </div>

              {/* Password requirements */}
              <div className="rounded-lg bg-bg-secondary p-4">
                <p className="mb-2 text-sm font-semibold text-text-primary">
                  Password Requirements:
                </p>
                <ul className="space-y-1 text-xs text-text-secondary">
                  <li>• At least 8 characters long</li>
                  <li>• Different from your current password</li>
                  <li>• Both password fields must match</li>
                </ul>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
